import React, { useRef, useState } from "react";
import { 
  Backdrop, 
  Button, 
  Grid, 
  styled, 
  Typography,
  Checkbox,
  FormControlLabel,
  Box
} from "@mui/material";

import { Link as RouterLink } from "react-router-dom";
import { Link as MuiLink} from "@mui/material";

const ModalContainer = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "90%",
  maxWidth: "700px", 
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "24px",
  [theme.breakpoints.down("sm")]: {
    padding: "16px",
  },
  margin: "1%",
  flexDirection: "column",
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  maxHeight: "350px", 
  overflowY: "auto",
  paddingRight: "16px",
  scrollbarGutter: "stable",
  marginTop: "20px",
  marginBottom: "24px",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#F9FAFB", 
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#D1D5DB",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: "#9CA3AF",
    }
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "8px",
}));

type Props = {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
};

export default function DataPrivacyModal({
  open,
  handleClose,
  handleConfirm,
}: Props) {
  const [isChecked, setIsChecked] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const onConfirmClick = () => {
    setIsChecked(false);
    handleConfirm();
  };

  const onCloseClick = () => {
    setIsChecked(false);
    handleClose();
  };

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const target = e.currentTarget;
    
    const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 5;
    
    if (isBottom) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <Backdrop
      sx={{ 
        backgroundColor: "rgba(0, 0, 0, 0.75)", 
        backdropFilter: "blur(2px)",
        zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <ModalContainer container={true}>
        <Grid item xs={12}>
          <Typography variant="h6" color="black" fontWeight="bold">
            Privacy Policy
          </Typography>
        </Grid>

        <ScrollableContent ref={scrollRef} onScroll={handleScroll}>
          <DataPrivacyContents />
        </ScrollableContent>
        {
          hasScrolledToBottom ?(
            <Grid item xs={12} >
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={isChecked} 
                    onChange={(e) => setIsChecked(e.target.checked)} 
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="black" fontWeight="medium">
                    Saya telah membaca dan menyetujui Kebijakan Privasi di atas.
                  </Typography>
                }
              />
            </Grid>
          ):(
            <Grid item xs={12} sx={{ textCenter: "center", p: 1 }}>
              <Typography variant="caption" sx={{ color: "#9CA3AF", fontStyle: "italic", display: "block", mb: 0.5 }}>
                * Silakan gulir (scroll) hingga ke bawah dokumen untuk membuka kotak persetujuan.
              </Typography>
            </Grid>
          )
        }
        <Typography variant="caption" sx={{ color: "#6B7280" }}>
          Selengkapnya, bisa dibuka di{" "}
          <MuiLink 
          component={RouterLink} 
          to="/Legal/TermsAndCondition"
          underline="hover" 
          sx={{ fontWeight: "bold", color: "#2563EB", cursor: "pointer" }}
              >
              link ini
          </MuiLink>                  
        </Typography> 
      
        <Grid 
          item 
          xs={12} 
          container 
          justifyContent="flex-end" 
          spacing={2} 
          sx={{ mt: 1 }}
        >
          <Grid item>
            <ActionButton 
              variant="text" 
              sx={{ color: "gray" }} 
              onClick={onCloseClick}
            >
              Tolak
            </ActionButton>
          </Grid>
          <Grid item>
            <ActionButton 
              variant="contained" 
              color="primary" 
              onClick={onConfirmClick}
              disabled={!isChecked}
            >
              Setujui Semua & Tutup
            </ActionButton>
          </Grid>
        </Grid>
      </ModalContainer>
    </Backdrop>
  );
}

const DataPrivacyContents = () => {
    return (
        <>
            <Typography 
                variant="body2" 
                color="textSecondary" 
                component="div" 
                sx={{ lineHeight: 1.6, textAlign: "justify" }}
            >
                <strong>1. Pendahuluan</strong><br />
                Kebijakan Privasi ini menjelaskan kebijakan dan prosedur PT Indomobil Sukses Internasional Tbk, termasuk anak-anak perusahaan dan afiliasinya (selanjutnya disebut "IMSI" atau "Kami") dalam melakukan pengumpulan, penyimpanan, penggunaan, dan penggunaan informasi Anda saat Anda menggunakan Aplikasi/Situs Web/Platform (“Layanan”) Kami, dimana Kami menjaga privasi Anda dengan serius dan akan memproses Data Pribadi Anda sesuai dengan peraturan perundang-undangan yang berlaku. Layanan Kami termasuk namun tidak terbatas pada Layanan yang dikembangkan oleh Kami yang telah Anda akses atau unduh di perangkat elektronik Anda, dengan tujuan untuk memberikan akses informasi, materi pembelajaran, dan layanan yang paling relevan dan berguna bagi Anda.
                <br /><br />
                Untuk sepenuhnya memahami hak-hak Anda, mohon agar Anda membaca Kebijakan Privasi ini untuk dapat mengetahui dan memahami bagaimana Kami melakukan pemrosesan Data Pribadi Anda. Kebijakan Privasi ini bersifat melengkapi tetapi tidak menghapus atau menggantikan setiap persetujuan lain yang mungkin telah Anda berikan sebelumnya kepada Kami sehubungan dengan Data Pribadi Anda. Persetujuan yang diberikan di sini merupakan tambahan untuk setiap hak yang mungkin dimiliki oleh Kami berdasarkan hukum yang berlaku untuk melakukan pemrosesan Data Pribadi Anda.
                <br /><br />

                <strong>2. Tujuan Kebijakan Privasi</strong><br />
                Kebijakan Privasi ini bertujuan untuk menjelaskan bahwa Data Pribadi Anda dapat digunakan dalam rangka penyediaan dan pengelolaan Layanan Kami, komunikasi dan penyampaian pembaruan, verifikasi dokumen, peningkatan mutu layanan, pelaksanaan riset pasar dan analisis, pencegahan penipuan dan tindak kejahatan, pemenuhan kewajiban hukum, penanganan klaim atau proses hukum, serta kegiatan pemasaran, promosi, dan tujuan lain yang secara wajar berkaitan dengan hal-hal tersebut. Penjelasan lebih rinci mengenai hal ini dapat Anda temukan pada bagian Tujuan Pengolahan Data Pribadi di bawah ini.
                <br /><br />

                <strong>3. Ruang Lingkup Pengelolaan Data Pribadi</strong><br />
                Kebijakan Privasi ini menjelaskan perihal Data Pribadi Anda yang Kami kumpulkan, menetapkan informasi penting mengenai pengumpulan, penggunaan, pemrosesan, pengungkapan dan penyimpanan Data Pribadi Anda, bagaimana dan kapan kami dapat menggunakan Data Pribadi Anda, bagaimana kami melindungi Data Pribadi Anda, siapa yang memiliki akses ke Data Pribadi Anda, dan kemampuan Anda untuk mengakses dan memperbaikinya.
                <br /><br />

                <strong>4. Dasar Hukum</strong><br />
                Data Pribadi Anda akan diproses berdasarkan dasar hukum terkait informasi elektronik dan Pelindungan Data Pribadi sebagai berikut:
                <ol style={{ margin: "4px 0", paddingLeft: "20px" }} type="a">
                    <li>Undang-Undang Republik Indonesia Nomor 11 Tahun 2008 tentang Informatika dan Transaksi Elektronik;</li>
                    <li>Undang-Undang Republik Indonesia No. 19 Tahun 2016 tentang Perubahan atas Undang-Undang Republik Indonesia No. 11 Tahun 2008 tentang Informatika dan Transaksi Elektronik;</li>
                    <li>Undang-Undang Republik Indonesia No. 1 Tahun 2024 tentang Perubahan Kedua atas Undang-Undang Republik Indonesia No. 11 Tahun 2008 tentang Informatika dan Transaksi Elektronik;</li>
                    <li>Peraturan Menteri Komunikasi dan Informatika Republik Indonesia No. 5 Tahun 2020 tentang Penyelenggara Sistem Elektronik Lingkup Swasta;</li>
                    <li>Peraturan Menteri Komunikasi dan Informatika Republik Indonesia No. 10 Tahun 2021 tentang Perubahan atas Peraturan Menteri Komunikasi dan Informatika Republik Indonesia No. 5 Tahun 2020 tentang Penyelenggara Sistem Elektronik Lingkup Swasta;</li>
                    <li>Peraturan Menteri Komunikasi dan Informatika Republik Indonesia No. 20 Tahun 2016 tentang Pelindungan Data Pribadi dalam Sistem Elektronik;</li>
                    <li>Undang-Undang Republik Indonesia No. 27 Tahun 2022 tentang Pelindungan Data Pribadi (“UU PDP”); dan</li>
                    <li>Peraturan terkait lainnya.</li>
                </ol>
                <br />

                <strong>5. Definisi</strong><br />
                <ol style={{ margin: "4px 0", paddingLeft: "20px" }} type="a">
                    <li><strong>Data Pribadi</strong> adalah setiap informasi tentang seseorang yang dapat mengidentifikasi orang tersebut, baik langsung maupun tidak langsung.</li>
                    <li><strong>Subjek Data Pribadi</strong> adalah orang perseorangan yang datanya diproses dan memiliki hak-hak yang dijamin undang-undang, seperti hak untuk mengakses, memperbarui, menghapus, atau menarik persetujuan atas pemrosesan data.</li>
                    <li><strong>Pengendali Data Pribadi (Data Controller)</strong> adalah pihak yang menentukan tujuan dan kendali atas pemrosesan Data Pribadi serta bertanggung jawab memastikan keamanan, keakuratan, dan transparansi pengelolaan data.</li>
                    <li><strong>Prosesor Data Pribadi (Data Processor)</strong> adalah pihak yang memproses Data Pribadi atas nama pengendali dan wajib tunduk pada instruksi serta menjaga kerahasiaan dan keamanan data.</li>
                </ol>
                <br />

                <strong>6. Jenis Data Pribadi yang Dikumpulkan</strong><br />
                Kami dapat mengumpulkan atau meminta Anda untuk memberikan informasi Data Pribadi tertentu yang dapat digunakan untuk menghubungi atau mengidentifikasi Anda ketika Anda memilih dan menggunakan Layanan yang telah kami sediakan. Data Pribadi yang dapat diidentifikasi dan diproses dapat mencakup Data Pribadi Umum dan Data Pribadi Spesifik, namun tidak terbatas pada:
                <ol style={{ margin: "4px 0", paddingLeft: "20px" }} type="a">
                    <li>Nama;</li>
                    <li>Alamat;</li>
                    <li>Tempat dan Tanggal lahir;</li>
                    <li>Jenis kelamin;</li>
                    <li>Profesi atau pekerjaan;</li>
                    <li>Alamat surat elektronik (E-mail);</li>
                    <li>Riwayat atau Status pendidikan;</li>
                    <li>Status pernikahan;</li>
                    <li>Nomor Telepon;</li>
                    <li>Jumlah anggota keluarga; dan</li>
                    <li>Informasi lain yang akan ditentukan oleh Kami.</li>
                </ol>
                Data lain mungkin telah dikumpulkan secara otomatis saat menggunakan Layanan Kami. Data lain dapat mencakup informasi seperti alamat protokol internet perangkat Anda (misalnya, alamat IP), jenis browser, versi browser, halaman layanan kami yang Anda kunjungi, waktu dan tanggal kunjungan Anda, waktu yang dihabiskan di halaman tersebut, pengidentifikasi perangkat unik, dan data diagnostik lainnya (Cookie).
                <br /><br />
                Ketika Anda mengakses Layanan dengan atau melalui perangkat seluler, kami dapat mengumpulkan informasi tertentu secara otomatis, termasuk, namun tidak terbatas pada, jenis perangkat seluler yang Anda gunakan, ID unik perangkat seluler Anda, alamat IP perangkat seluler Anda, sistem operasi perangkat seluler Anda, jenis browser Internet seluler yang Anda gunakan, pengidentifikasi perangkat unik, dan data diagnostik lainnya (Cookie).
                <br /><br />
                Kami juga dapat mengumpulkan informasi yang dikirimkan browser Anda setiap kali Anda mengunjungi Layanan kami atau mengakses layanan dengan atau melalui perangkat seluler. Cookie memungkinkan kami untuk melacak dan mengumpulkan data perilaku pengguna sehingga kami dapat menargetkan minat pengguna kami dengan lebih baik dan meningkatkan pengalaman mereka di Situs.
                <br /><br />
                Anda dapat mengatur browser Anda untuk menolak semua cookie atau untuk menunjukkan kapan cookie sedang dikirim. Namun, beberapa fitur atau Layanan mungkin tidak berfungsi dengan baik tanpa cookie mengingat beberapa halaman web, pesan e-mail, dan/atau buletin kami mungkin berisi gambar elektronik yang dapat terganggu dengan penolakan cookie tersebut.
                <br /><br />

                <strong>7. Tujuan Pengolahan Data Pribadi</strong><br />
                Kami dapat menggunakan Data Pribadi untuk tujuan berikut:
                <ol style={{ margin: "4px 0", paddingLeft: "20px" }} type="a">
                    <li>Untuk mengoperasikan dan memelihara Layanan Kami, termasuk memantau penggunaannya, administrasi sistem, statistik, dan tujuan pemecahan masalah.</li>
                    <li>Untuk pelaksanaan perjanjian: pembuatan, kepatuhan, dan pelaksanaan pembelian untuk produk, barang, atau layanan yang dapat Anda tukarkan atau beli melalui Layanan atau perjanjian lain dengan Kami, sesuai kondisinya.</li>
                    <li>Untuk menghubungi Anda: Untuk menghubungi Anda melalui e-mail, panggilan telepon, SMS, atau metode komunikasi elektronik lain yang setara, seperti pemberitahuan dari ponsel Anda, mengenai pembaruan atau komunikasi informatif yang berkaitan dengan fungsi, produk, atau layanan lain, termasuk pembaruan keamanan, sebagaimana diperlukan atau wajar untuk implementasinya.</li>
                    <li>Untuk memberi Anda berita, penawaran khusus, dan informasi umum tentang barang, layanan, dan acara lain yang dapat ditawarkan yang serupa, kecuali Anda memilih untuk tidak menerima informasi tersebut.</li>
                    <li>Untuk mengirimkan survei kepuasan mengenai layanan yang Kami berikan untuk meningkatkan layanan Kami kepada Anda.</li>
                    <li>Untuk membantu Anda mengelola permintaan: Untuk menanggapi dan mengelola pertanyaan Anda kepada Kami.</li>
                    <li>Untuk transfer bisnis: Kami dapat menggunakan Data Pribadi Anda dalam hal terjadi suatu evaluasi atau melakukan merger, divestasi, akuisisi, restrukturisasi, reorganisasi, pembubaran, atau penjualan atau transfer beberapa atau semua aset Kami, baik sebagai operasional atau sebagai bagian dari aset, dimana Data Pribadi tentang pengguna Layanan Kami adalah salah satu aset yang ditransfer.</li>
                    <li>Kami dapat menggunakan Data Pribadi Anda untuk tujuan tambahan, seperti analisis data, mengidentifikasi tren penggunaan, menentukan efektivitas kampanye promosi Kami, dan mengevaluasi serta meningkatkan layanan, produk, layanan, pemasaran, dan pengalaman Kami.</li>
                    <li>Kami dapat menggunakan Data Pribadi Anda untuk menyesuaikan pengalaman Anda di Layanan kami, seperti menampilkan konten yang menurut Kami mungkin Anda minati dan menampilkan konten yang sesuai dengan preferensi Anda.</li>
                </ol>
                Kami dapat membagikan Data Pribadi Anda dalam situasi berikut:
                <ol style={{ margin: "4px 0", paddingLeft: "20px" }} type="a">
                    <li>Kepada prosesor Data Pribadi Kami: Kami dapat membagikan Data Pribadi Anda dengan prosesor Data Pribadi untuk memantau dan menganalisis bagaimana layanan kami digunakan, dan untuk menghubungi Anda.</li>
                    <li>Dengan Afiliasi: Jika kami membagikan Data Pribadi Anda dengan afiliasi Kami, Kami akan meminta mereka untuk mematuhi Kebijakan Privasi ini. Afiliasi didefinisikan sebagai perusahaan induk Kami dan setiap anak perusahaan, mitra usaha patungan, atau bisnis lain yang Kami kendalikan atau dikendalikan oleh Kami.</li>
                    <li>Dengan mitra bisnis: Kami dapat membagikan informasi Anda dengan mitra bisnis Kami untuk menawarkan produk, layanan, atau promosi tertentu kepada Anda, Kami akan meminta mereka untuk mematuhi Kebijakan Privasi ini.</li>
                    <li>Dengan pengguna lain: Saat Anda berbagi Data Pribadi atau berinteraksi dengan pengguna lain, informasi tersebut dapat dilihat oleh semua pengguna dan dapat didistribusikan secara publik.</li>
                    <li>Untuk Penegakan Hukum: Kami dapat membagikan Data Pribadi Anda dengan Penegak Hukum bila perlu untuk mengungkapkan Data Pribadi tersebut.</li>
                    <li>Dengan persetujuan Anda: Dengan persetujuan Anda, kami dapat mengungkapkan informasi pribadi Anda untuk tujuan lain.</li>
                </ol>
                <br />

                <strong>8. Dasar Pemrosesan Data</strong><br />
                Kami mengumpulkan dan memproses Data Pribadi Anda semata-mata berdasarkan persetujuan yang sah dari Anda, serta sesuai dengan dasar pemrosesan Data Pribadi lain sesuai dengan ketentuan peraturan perundang-undangan yang berlaku di Negara Republik Indonesia, termasuk namun tidak terbatas pada UU PDP.
                <br /><br />

                <strong>9. Hak-Hak Subjek Data Pribadi</strong><br />
                Jika Anda telah menyatakan persetujuan Anda untuk aktivitas pemrosesan Data Pribadi apa pun, Anda dapat menarik persetujuan ini kapan saja dengan efek di masa mendatang. Penarikan tersebut tidak akan memengaruhi keabsahan pemrosesan sebelum penarikan persetujuan. Berdasarkan peraturan perundang-undangan yang berlaku di Negara Republik Indonesia, Anda berhak untuk:
                <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                    <li>meminta akses ke Data Pribadi Anda;</li>
                    <li>meminta perbaikan Data Pribadi Anda;</li>
                    <li>meminta Salinan Data Pribadi Anda;</li>
                    <li>meminta penghapusan Data Pribadi Anda;</li>
                    <li>menarik persetujuan Data Pribadi Anda;</li>
                    <li>memberi keluhan atas Data Pribadi Anda; dan</li>
                    <li>meminta pembatasan Data Pribadi Anda.</li>
                </ul>
                Harap dicatat bahwa hak-hak yang disebutkan di atas mungkin dibatasi sesuai dengan ketentuan peraturan perundang-undangan yang berlaku di Negara Republik Indonesia.
                <br /><br />

                <strong>10. Penyimpanan dan Retensi Data</strong><br />
                Data Pribadi Anda disimpan oleh Kami dan/atau penyedia layanan Kami, secara ketat sejauh yang diperlukan untuk pelaksanaan kewajiban Kami dan secara ketat selama waktu yang diperlukan untuk mencapai tujuan pengumpulan Data Pribadi, sesuai dengan undang-undang perlindungan data yang berlaku.
                <br /><br />
                Setelah Anda mengakhiri akun Anda, Data Pribadi Anda tidak akan disimpan. Kami akan menghapusnya dari sistem dan catatan kami dalam waktu 10 (sepuluh) tahun sejak pendaftaran Anda, atau ketika Anda menggunakan hak Anda untuk dilupakan, atau ketika kami menganonimkannya dengan benar sehingga Anda tidak dapat diidentifikasi. Namun, jika kami diwajibkan oleh hukum atau peraturan yang berlaku, seperti persyaratan penyimpanan untuk Data Pribadi terkait perjanjian, komunikasi, atau bisnis, kami mungkin perlu menyimpan informasi Anda hingga 10 (sepuluh) tahun.
                <br /><br />

                <strong>11. Transfer Data Pribadi</strong><br />
                Pengendali dan/atau Prosesor Data Pribadi Anda mungkin dapat berlokasi di luar wilayah Negara Republik Indonesia. Setelah Data Pribadi Anda ditransfer ke luar negeri (apabila dilakukan), Kami akan memberi tahu Anda secara terpisah sejauh yang diperlukan berdasarkan hukum dan peraturan perundang-undangan yang berlaku. Berkenaan dengan transfer data ke penerima tersebut di luar wilayah Negara Republik Indonesia, kami akan memberikan perlindungan terhadap Data Pribadi Anda yang sesuai, khususnya, dengan cara menandatangani perjanjian transfer data atau mengambil langkah-langkah lain untuk memberikan tingkat perlindungan data yang memadai (sesuai dengan pelaksanaannya).
                <br /><br />
                Kami akan mematuhi semua prosedur yang diatur oleh peraturan perundang-undangan yang berlaku dan implementasinya.
                <br /><br />

                <strong>12. Pihak Ketiga & Pemrosesan Data</strong><br />
                Layanan Kami dapat memuat tautan ke situs web, aplikasi, atau platform pihak ketiga yang tidak dioperasikan oleh Kami. Jika Anda menekan atau menuju tautan tersebut, Anda akan diarahkan ke layanan pihak ketiga. Kami tidak memiliki kendali atas, serta tidak bertanggung jawab terhadap, konten, kebijakan privasi, maupun praktik yang diterapkan oleh pihak ketiga tersebut.
                <br /><br />
                Beberapa tautan mungkin menuju ke layanan yang dimiliki atau dioperasikan oleh penyedia pihak ketiga, termasuk yang memiliki afiliasi dengan Kami. Setiap situs web, aplikasi, atau platform pihak ketiga memiliki kebijakan privasi dan praktik keamanan masing-masing, yang sepenuhnya menjadi tanggung jawab pemilik atau pengelolanya. Oleh karena itu, Kami menyarankan Anda untuk membaca dan memahami kebijakan privasi dari setiap situs web, aplikasi, atau platform pihak ketiga yang Anda kunjungi.
                <br /><br />

                <strong>13. Perubahan Kebijakan Privasi</strong><br />
                Kami dapat memperbarui Kebijakan Privasi ini berdasarkan kebijakan Kami dari waktu ke waktu untuk mencerminkan perubahan dalam praktik pemrosesan Data Pribadi Kami atau untuk memenuhi persyaratan hukum. Kami akan mempublikasikan perubahan tersebut pada halaman ini dan, jika diperlukan, memberi tahu Anda melalui e-mail atau melalui pemberitahuan di Layanan Kami.
                <br /><br />

                <strong>14. Data Pribadi Anak dan Penyandang Disabilitas</strong><br />
                Layanan kami dapat ditujukan dan digunakan untuk anak-anak di bawah usia 18 (delapan belas) tahun. Kami tidak mengumpulkan informasi Data Pribadi dari anak-anak di bawah usia 18 (delapan belas) tahun tanpa persetujuan orang tua mereka. Jika Anda adalah orang tua atau wali dan Anda yakin anak Anda mungkin telah memberikan Data Pribadi kepada kami, silakan hubungi Kami.
                <br /><br />
                Kami dapat mengumpulkan dan menggunakan Data Pribadi Anda dengan persetujuan orang tua Anda. Jika kami mengetahui bahwa kami telah mengumpulkan Data Pribadi dari siapa pun yang berusia di bawah 18 (delapan belas) tahun tanpa memperoleh persetujuan orang tua, Kami akan segera mengambil langkah-langkah untuk menghapus informasi tersebut dari tempat Data Pribadi tersebut disimpan.
                <br /><br />
                Untuk penyandang disabilitas, Kami akan mengumpulkan Data Pribadi dengan terlebih dahulu mendapatkan persetujuan Anda dan/atau melalui wali Anda dan dengan mekanisme sesuai dengan ketentuan perundang-undangan yang berlaku.
            </Typography>
        </>
    );
};